import L from 'leaflet';
import wellknown from 'wellknown';
import gjv from 'geojson-validation';

import bbox from '@turf/bbox';
import { feature } from '@turf/helpers';
import truncate from '@turf/truncate';

import kmlToGeoJSON from '@mapbox/togeojson';
import i18n from '@/i18n';
import PdfSheetPlugin from '@/plugins/pdf-sheet-plugin/pdf-sheet-plugin';

const PDF_OPTIONS = ['orientation', 'sizeX', 'sizeY', 'scale'];

const addPdfPlugin = (map, value) => {
  const pdfSheetPlugin = new PdfSheetPlugin({
    position: 'bottomleft',
    locale: {
      mm: i18n.t('product.pdf.mm'),
      Scale: i18n.t('product.pdf.scale'),
      Size: i18n.t('product.pdf.size'),
      Orientation: i18n.t('product.pdf.orientation'),
      scale_unit: i18n.t('product.pdf.scaleUnit'),
    },
  });
  if (value) {
    pdfSheetPlugin.addGeomToFit(L.geoJSON(value));
  }
  pdfSheetPlugin.addTo(map);
  return pdfSheetPlugin;
};

const removePdfPlugin = (map, plugin) => {
  map.removeControl(plugin);
};

const removeLayer = (map, layer) => {
  map.removeLayer(layer);
};

const formatPdfOptions = (options, rec) => {
  const optionsCopy = { ...options };
  if (optionsCopy.orientation !== 'album') {
    const sizeXOld = optionsCopy.sizeX;
    optionsCopy.sizeX = optionsCopy.sizeY;
    optionsCopy.sizeY = sizeXOld;
  }
  const filteredOptions = Object.fromEntries(
    Object.entries(optionsCopy).filter(([key, _value]) => PDF_OPTIONS.indexOf(key) > -1),
  );
  return {
    ...filteredOptions,
    polygon: wellknown.stringify(rec.toGeoJSON()),
  };
};

const drawnGeojsonToWkt = (geojson) => {
  // wraping geometries into features with turf.feature()
  const data = geojson.type === 'Polygon' || geojson.type === 'MultiPolygon' ? feature(geojson) : geojson;
  return wellknown.stringify(data);
};

const wktToGeojson = (wkt) => {
  return { type: 'Feature', properties: {}, geometry: wellknown.parse(wkt) };
};

const getMapObject = (el) => el.mapObject;
const getGeoJSONLayer = (data) => L.geoJSON(data);
const isSameLayer = (layerA, layerB) => L.stamp(layerA) === L.stamp(layerB);
const toGeoJSON = (data) => data.toGeoJSON();
const startPolygon = (mapObject) => mapObject.editTools.startPolygon();
const startRectangle = (mapObject) => mapObject.editTools.startRectangle();
const stopDrawing = (mapObject) => {
  mapObject.editTools.stopDrawing();
};
const disableEditing = (layer) => {
  layer.editor.disable();
};
const enableEditing = (layer) => {
  layer.editor.enable();
};
const getVertextCountOfEditableLayer = (layer) => {
  if (layer && layer.editor) {
    // eslint-disable-next-line no-underscore-dangle
    return layer.editor._drawnLatLngs.length;
  }
  throw new Error('layer is not editable');
};

const isSomethingDrawn = ({ layer, isRectangleDrawingMode }) => {
  const drawnVertexCount = !!layer && getVertextCountOfEditableLayer(layer);
  return isRectangleDrawingMode
    ? drawnVertexCount > 3 // leaflet creates editableLayer with 3 features before user clicks on the map for drawing rectangle
    : drawnVertexCount > 0;
};

const shouldCompletePolygonHintBeShown = ({ layer, isRectangleDrawingMode }) => {
  return isRectangleDrawingMode ? false : !!layer && getVertextCountOfEditableLayer(layer) >= layer.editor.MIN_VERTEX;
};
const fitBounds = (mapObject, bounds) => {
  mapObject.fitBounds(bounds);
};
const fitBoundsToGeojson = (mapObject, geojson) => {
  const bounds = getGeoJSONLayer(geojson).getBounds();
  mapObject.fitBounds(bounds);
};

const isMultiPolygon = (geojson) => {
  return !!geojson && !!geojson.geometry && geojson.geometry.type === 'MultiPolygon';
};
const geojsonPolygonToLatLng = (geojson) => {
  // function doesn't work with multipolygon
  if (isMultiPolygon(geojson)) {
    throw new Error("geojsonPolygonToLatLng function doesn't work with multipolygon");
  }
  return geojson.geometry && geojson.geometry.coordinates
    ? L.GeoJSON.coordsToLatLngs(geojson.geometry.coordinates[0]).map(({ lng, lat }) => [lat, lng])
    : null;
};

const regionBoundsToLatLngBounds = (regionBounds) =>
  L.latLngBounds([
    [regionBounds.y_max, regionBounds.x_max],
    [regionBounds.y_min, regionBounds.x_min],
  ]);

const getCenterFromBounds = (latLngBounds) => latLngBounds.getCenter();

const isSupportedGeoJsonFormat = (geojson) => {
  return (
    gjv.valid(geojson) &&
    (gjv.isFeature(geojson) ||
      gjv.isFeatureCollection(geojson) ||
      gjv.isGeometryCollection(geojson) ||
      gjv.isGeometryObject(geojson))
  );
};

const isKml = (file) => {
  return file.name.toLowerCase().endsWith('.kml');
};

const isKmz = (file) => {
  return file.name.toLowerCase().endsWith('.kmz');
};

const outOfSphericalCoordsRange = (geojson) => {
  const [minLon, minLat, maxLon, maxLat] = bbox(geojson);
  const isFitting =
    Math.abs(minLat) <= 90 && Math.abs(maxLat <= 90) && Math.abs(minLon) <= 180 && Math.abs(maxLon <= 180);
  return !isFitting;
};

const isUnsupportedCRS = (geojson) => {
  const definedWrongCRS =
    // eslint-disable-next-line no-prototype-builtins
    geojson.hasOwnProperty('crs') && geojson.crs.properties.name !== 'urn:ogc:def:crs:OGC:1.3:CRS84';
  return definedWrongCRS || outOfSphericalCoordsRange(geojson);
};

const isPoly = (featureToTest) => {
  return featureToTest.type === 'Polygon' || featureToTest.type === 'MultiPolygon';
};

const isSupportedGeometry = (geojson) => {
  const { type } = geojson;

  switch (type) {
    case 'FeatureCollection':
      return (
        geojson.features.length === 1 && geojson.features.every((geojsonFeature) => isPoly(geojsonFeature.geometry))
      );
    case 'Feature':
      return geojson.geometry.coordinates.length > 0 && isPoly(geojson.geometry);
    case 'GeometryCollection':
      return geojson.geometries.length === 1 && geojson.geometries.every(isPoly);
    case 'Polygon':
      return geojson.coordinates.length > 0;
    case 'MultiPolygon':
      return geojson.coordinates.length > 0;
    default:
      console.log('Currentrly unsupported or generally wrong file: ', geojson);
      return false;
  }
};

const removeZCoord = (geojson) => truncate(geojson, { precision: 16, coordinates: 2 });

const getGeometryByType = (geojson) => {
  const { type } = geojson;

  const cleanedGeoJson = removeZCoord(geojson);

  switch (type) {
    case 'FeatureCollection':
      return cleanedGeoJson.features[0];
    case 'Feature':
      return cleanedGeoJson;
    case 'GeometryCollection':
      return cleanedGeoJson.geometries[0];
    case 'Geometry': // maybe useless
      return cleanedGeoJson;
    case 'Polygon':
      return cleanedGeoJson;
    case 'MultiPolygon':
      return cleanedGeoJson;
    default:
      throw new Error();
  }
};

const isZeroSizePolygon = (geojson) => {
  return geojson.geometry.coordinates[0].every(([x, y], _i, all) => {
    return x === all[0][0] && y === all[0][1];
  });
};

const fixZeroSizeRectangle = (geojson, zoom) => {
  const offset = 300 / (zoom + 1) ** 3;
  const spreadPointsGeoJson = {
    ...geojson,
    geometry: {
      type: 'Polygon',
      coordinates: [
        geojson.geometry.coordinates[0].map(([x, y], i) => {
          if (i === 0 || i === 4) return [x, y];
          if (i === 1) return [x + offset, y];
          if (i === 2) return [x + offset, y - offset];
          return [x, y - offset];
        }),
      ],
    },
  };

  return spreadPointsGeoJson;
};

export default {
  addPdfPlugin,
  removePdfPlugin,
  removeLayer,
  formatPdfOptions,
  getMapObject,
  getGeoJSONLayer,
  geojsonPolygonToLatLng,
  isSameLayer,
  toGeoJSON,
  startPolygon,
  startRectangle,
  stopDrawing,
  disableEditing,
  enableEditing,
  getVertextCountOfEditableLayer,
  isSomethingDrawn,
  shouldCompletePolygonHintBeShown,
  fitBounds,
  fitBoundsToGeojson,
  drawnGeojsonToWkt,
  wktToGeojson,
  isMultiPolygon,
  regionBoundsToLatLngBounds,
  getCenterFromBounds,
  isSupportedGeoJsonFormat,
  isUnsupportedCRS,
  isSupportedGeometry,
  getGeometryByType,
  removeZCoord,
  kmlToGeoJSON,
  isKml,
  isKmz,
  isZeroSizePolygon,
  fixZeroSizeRectangle,
};
